import React, { useState, useEffect } from 'react';
import { SoulClipItem } from '../studio/studioUtils';
import { EmotionMood, Clip, SoulEntry } from '../../types';
import { fetchSharedClips, toggleClipLike, recordClipView, deleteSharedClip } from '../../services/shareService';
import { CharacterAvatar } from '../common/CharacterAvatar';
import { FollowButton } from '../common/FollowButton';
import { ShareSoulClipModal } from './ShareSoulClipModal';
import { FriendsPanel } from './FriendsPanel';
import { ReviewShareLightbox } from './ReviewShareLightbox';
import {
  Share2,
  Inbox,
  Globe,
  Clock,
  Eye,
  Heart,
  Target,
  Search,
  Scissors,
  Music,
  Play,
  Copy,
  Check,
  RefreshCw,
  Sparkles,
  Lock,
  Users,
  Trash2
} from 'lucide-react';

interface SoulShareGalleryProps {
  currentUserName?: string;
  stagedClip?: Clip | SoulClipItem | null;
  stagedEntry?: SoulEntry | null;
  onClearStagedClip?: () => void;
  onOpenClipInStudio?: (clip: SoulClipItem, mode?: 'make_it_mine' | 'colab') => void;
  onNewClip?: () => void;
  onOpenShareModal?: (clip: SoulClipItem) => void;
  onSelectClip?: (clip: SoulClipItem) => void;
  friendsPanelSlot?: React.ReactNode;
  activeFriendFilter?: string | null;
  onClearFriendFilter?: () => void;
}

export const SoulShareGallery: React.FC<SoulShareGalleryProps> = ({
  currentUserName = '',
  stagedClip,
  stagedEntry,
  onClearStagedClip,
  onOpenClipInStudio,
  onNewClip,
  onOpenShareModal,
  onSelectClip,
  friendsPanelSlot,
  activeFriendFilter,
  onClearFriendFilter
}) => {
  // Navigation Tabs: My Shares, Friends, Following, Community Showcase
  const [activeTab, setActiveTab] = useState<'my_shares' | 'direct' | 'following' | 'community'>('my_shares');

  // 4-Way Sorting: newest, most_viewed, most_liked, completion
  const [sortBy, setSortBy] = useState<'newest' | 'most_viewed' | 'most_liked' | 'completion'>('newest');

  // Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedMood, setSelectedMood] = useState<string>('all');
  const [stemsOnly, setStemsOnly] = useState<boolean>(false);
  const [internalFriendFilter, setInternalFriendFilter] = useState<string | null>(null);
  const effectiveFriendFilter = activeFriendFilter !== undefined ? activeFriendFilter : internalFriendFilter;

  // Data & Status
  const [clips, setClips] = useState<SoulClipItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [directCount, setDirectCount] = useState<number>(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [sharingClip, setSharingClip] = useState<SoulClipItem | null>(null);
  // Delete flow: first click → confirm state (shows red label), second click → execute
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeletingInFlight, setIsDeletingInFlight] = useState<boolean>(false);

  // Lightbox Player State
  const [lightboxClip, setLightboxClip] = useState<Clip | SoulClipItem | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [lightboxMode, setLightboxMode] = useState<'my_shares' | 'community' | 'direct' | 'following'>('my_shares');

  // When a clip is shared from SoulSync (stagedClip), open SoulShare with it in Lightbox
  useEffect(() => {
    if (stagedClip) {
      setLightboxClip(stagedClip);
      setLightboxMode('my_shares');
      setIsLightboxOpen(true);
      setActiveTab('my_shares');
      onClearStagedClip?.();
    }
  }, [stagedClip, onClearStagedClip]);

  const loadClips = async () => {
    setIsLoading(true);
    try {
      const u = currentUserName?.trim() || 'Dan';

      const fetched = await fetchSharedClips({
        mode: activeTab,
        user: u || undefined,
        friend: effectiveFriendFilter || undefined,
        sortBy,
        search: searchQuery
      });
      setClips(fetched);

      // Pre-fetch count of direct shares for badge
      if (u) {
        if (activeTab !== 'direct') {
          fetchSharedClips({ mode: 'direct', user: u }).then((res) => {
            setDirectCount(res.length);
          });
        } else {
          setDirectCount(fetched.length);
        }
      } else {
        setDirectCount(0);
      }
    } catch (err) {
      console.warn('[SoulShareGallery] fetch clips error:', err);
      setClips([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadClips();
  }, [activeTab, sortBy, searchQuery, effectiveFriendFilter, currentUserName]);

  const handleLikeClick = async (e: React.MouseEvent, clip: SoulClipItem) => {
    e.stopPropagation();
    // Optimistic UI update
    setClips((prev) =>
      prev.map((c) => (c.id === clip.id ? { ...c, likesCount: (c.likesCount || 0) + 1 } : c))
    );
    await toggleClipLike(clip.id);
  };

  const handleCopyLink = (e: React.MouseEvent, clip: SoulClipItem) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/#clip=${clip.id}`;
    navigator.clipboard.writeText(shareUrl).catch(() => {});
    setCopiedId(clip.id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  // Clicking an entry card opens its Lightbox Player with context-aware controls
  const handleCardClick = (clip: SoulClipItem) => {
    setLightboxClip(clip);
    setLightboxMode(activeTab);
    setIsLightboxOpen(true);
  };

  // Client-side mood and stems filtering
  const filteredClips = clips.filter((clip) => {
    if (stemsOnly && !clip.hasStems) return false;
    return true;
  });

  return (
    <div className="studio-wrapper">
      {/* ── Main Tab Navigation (Order: My Shares first, Friends second, Community Showcase third) ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          paddingBottom: '8px',
          flexWrap: 'wrap',
          gap: '8px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Tab 1: My Shares */}
          <button
            type="button"
            onClick={() => setActiveTab('my_shares')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '10px',
              background: activeTab === 'my_shares' ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(99, 102, 241, 0.18) 100%)' : 'transparent',
              border: activeTab === 'my_shares' ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid transparent',
              color: activeTab === 'my_shares' ? '#f3e8ff' : '#94a3b8',
              fontSize: '0.88rem',
              fontWeight: activeTab === 'my_shares' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <Share2 size={16} color={activeTab === 'my_shares' ? '#c084fc' : '#94a3b8'} />
            <span>My Shares</span>
          </button>

          {/* Tab 2: Friends Tab (Repurposed from Shared With Me) */}
          <button
            type="button"
            onClick={() => setActiveTab('direct')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '10px',
              background: activeTab === 'direct' ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(99, 102, 241, 0.18) 100%)' : 'transparent',
              border: activeTab === 'direct' ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid transparent',
              color: activeTab === 'direct' ? '#f3e8ff' : '#94a3b8',
              fontSize: '0.88rem',
              fontWeight: activeTab === 'direct' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <Users size={16} color={activeTab === 'direct' ? '#c084fc' : '#94a3b8'} />
            <span>Friends</span>
            {directCount > 0 && (
              <span
                style={{
                  fontSize: '0.68rem',
                  padding: '1px 6px',
                  borderRadius: '9999px',
                  background: 'rgba(168, 85, 247, 0.4)',
                  color: '#ffffff',
                  fontWeight: 700
                }}
              >
                {directCount}
              </span>
            )}
          </button>

          {/* Tab 3: Following Tab (1-Way Creator Feed) */}
          <button
            type="button"
            onClick={() => setActiveTab('following')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '10px',
              background: activeTab === 'following' ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.22) 0%, rgba(168, 85, 247, 0.18) 100%)' : 'transparent',
              border: activeTab === 'following' ? '1px solid rgba(236, 72, 153, 0.5)' : '1px solid transparent',
              color: activeTab === 'following' ? '#fdf2f8' : '#94a3b8',
              fontSize: '0.88rem',
              fontWeight: activeTab === 'following' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <Heart size={16} color={activeTab === 'following' ? '#f472b6' : '#94a3b8'} />
            <span>Following</span>
          </button>

          {/* Tab 4: Community Showcase */}
          <button
            type="button"
            onClick={() => setActiveTab('community')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '10px',
              background: activeTab === 'community' ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.22) 0%, rgba(99, 102, 241, 0.15) 100%)' : 'transparent',
              border: activeTab === 'community' ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid transparent',
              color: activeTab === 'community' ? '#dbeafe' : '#94a3b8',
              fontSize: '0.88rem',
              fontWeight: activeTab === 'community' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <Globe size={16} color={activeTab === 'community' ? '#60a5fa' : '#94a3b8'} />
            <span>Community Showcase</span>
          </button>
        </div>

        {/* Compact Refresh Action */}
        <button
          type="button"
          onClick={loadClips}
          className="btn-ghost"
          style={{
            padding: '0.45rem 0.8rem',
            fontSize: '0.78rem',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderRadius: '8px'
          }}
          title="Refresh shared clips"
        >
          <RefreshCw size={13} className={isLoading ? 'spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* ── Active Friend Filter Pill (if user filtered by clicking a friend) ── */}
      {effectiveFriendFilter && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '8px',
            background: 'rgba(168, 85, 247, 0.2)',
            border: '1px solid rgba(168, 85, 247, 0.4)',
            color: '#e9d5ff',
            fontSize: '0.8rem',
            fontWeight: 600,
            width: 'fit-content'
          }}
        >
          <span>Filtered by friend: <strong>@{effectiveFriendFilter}</strong></span>
          <button
            type="button"
            onClick={() => {
              setInternalFriendFilter(null);
              onClearFriendFilter?.();
            }}
            style={{ background: 'transparent', border: 'none', color: '#c084fc', cursor: 'pointer', padding: 0, display: 'flex' }}
            title="Clear friend filter"
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Main Content Area (Lists shared entries; clicks open Lightbox Player) ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', width: '100%' }}>
        {/* ── Friends & Crew Panel (Rendered prominently on the Friends Tab) ── */}
        {activeTab === 'direct' && (
          <div style={{ width: '100%' }}>
            {friendsPanelSlot || (
              <FriendsPanel
                currentUserName={currentUserName}
                activeFriendFilter={effectiveFriendFilter}
                onSelectFriend={(friend) => setInternalFriendFilter(friend)}
                onClearFriendFilter={() => {
                  setInternalFriendFilter(null);
                  onClearFriendFilter?.();
                }}
              />
            )}
          </div>
        )}

        {/* ── 4-Way Sorting & Filters Toolbar ── */}
        <div className="gallery-filters" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          {/* 4-Way Sort Buttons (User requested: Newest, Most Viewed, More Likes, Completion x/y) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, marginRight: '3px' }}>
              Sort:
            </span>

            {/* 1. Newest */}
            <button
              type="button"
              onClick={() => setSortBy('newest')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 9px',
                borderRadius: '6px',
                background: sortBy === 'newest' ? 'rgba(168, 85, 247, 0.28)' : 'rgba(255, 255, 255, 0.05)',
                border: sortBy === 'newest' ? '1px solid #c084fc' : '1px solid rgba(255, 255, 255, 0.1)',
                color: sortBy === 'newest' ? '#f3e8ff' : '#cbd5e1',
                fontSize: '0.74rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <Clock size={12} color={sortBy === 'newest' ? '#c084fc' : '#94a3b8'} />
              <span>Newest</span>
            </button>

            {/* 2. Most Viewed */}
            <button
              type="button"
              onClick={() => setSortBy('most_viewed')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 9px',
                borderRadius: '6px',
                background: sortBy === 'most_viewed' ? 'rgba(168, 85, 247, 0.28)' : 'rgba(255, 255, 255, 0.05)',
                border: sortBy === 'most_viewed' ? '1px solid #c084fc' : '1px solid rgba(255, 255, 255, 0.1)',
                color: sortBy === 'most_viewed' ? '#f3e8ff' : '#cbd5e1',
                fontSize: '0.74rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <Eye size={12} color={sortBy === 'most_viewed' ? '#c084fc' : '#94a3b8'} />
              <span>Most Viewed</span>
            </button>

            {/* 3. More Likes */}
            <button
              type="button"
              onClick={() => setSortBy('most_liked')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 9px',
                borderRadius: '6px',
                background: sortBy === 'most_liked' ? 'rgba(236, 72, 153, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                border: sortBy === 'most_liked' ? '1px solid #f472b6' : '1px solid rgba(255, 255, 255, 0.1)',
                color: sortBy === 'most_liked' ? '#fce7f3' : '#cbd5e1',
                fontSize: '0.74rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <Heart size={12} color={sortBy === 'most_liked' ? '#f472b6' : '#94a3b8'} />
              <span>More Likes</span>
            </button>

            {/* 4. Completion Amount (x/y) */}
            <button
              type="button"
              onClick={() => setSortBy('completion')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 9px',
                borderRadius: '6px',
                background: sortBy === 'completion' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                border: sortBy === 'completion' ? '1px solid #34d399' : '1px solid rgba(255, 255, 255, 0.1)',
                color: sortBy === 'completion' ? '#d1fae5' : '#cbd5e1',
                fontSize: '0.74rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <Target size={12} color={sortBy === 'completion' ? '#34d399' : '#94a3b8'} />
              <span>Completion (x/y)</span>
            </button>
          </div>

          {/* Live Search & Stems Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setStemsOnly(!stemsOnly)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 9px',
                borderRadius: '6px',
                background: stemsOnly ? 'rgba(52, 211, 153, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: stemsOnly ? '1px solid #34d399' : '1px solid rgba(255, 255, 255, 0.1)',
                color: stemsOnly ? '#6ee7b7' : '#94a3b8',
                fontSize: '0.72rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <Music size={11} />
              <span>Stems Ready</span>
            </button>

            <div style={{ position: 'relative', minWidth: '180px' }}>
              <Search
                size={12}
                style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search scene, creator..."
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '6px',
                  padding: '4px 8px 4px 26px',
                  color: '#ffffff',
                  fontSize: '0.75rem',
                  outline: 'none'
                }}
              />
            </div>
          </div>
        </div>

        {/* Gallery Grid: Following the player we see the list of shared clips */}
        <div>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8', fontSize: '0.9rem' }}>
              <RefreshCw size={20} className="spin" style={{ marginBottom: '8px' }} />
              <div>Loading SoulShare clips...</div>
            </div>
          ) : filteredClips.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '12px',
                border: '1px dashed rgba(255, 255, 255, 0.12)',
                color: '#94a3b8'
              }}
            >
              {activeTab === 'direct' ? (
                <Users size={32} color="#a855f7" style={{ marginBottom: '12px', opacity: 0.7 }} />
              ) : activeTab === 'following' ? (
                <Heart size={32} color="#f472b6" style={{ marginBottom: '12px', opacity: 0.7 }} />
              ) : (
                <Share2 size={32} color="#a855f7" style={{ marginBottom: '12px', opacity: 0.7 }} />
              )}
              <div style={{ fontSize: '1rem', fontWeight: 600, color: '#e2e8f0', marginBottom: '6px' }}>
                {activeTab === 'my_shares'
                  ? !currentUserName?.trim()
                    ? 'Sign in to view your shares'
                    : "You haven't shared any clips yet"
                  : activeTab === 'direct'
                  ? !currentUserName?.trim()
                    ? 'Sign in to see friend collabs'
                    : 'No scenes shared with friends yet'
                  : activeTab === 'following'
                  ? !currentUserName?.trim()
                    ? 'Sign in to see followed creators'
                    : 'No clips from followed creators yet'
                  : 'No community clips match your search'}
              </div>
              <p style={{ fontSize: '0.8rem', maxWidth: '440px', margin: '0 auto 1.25rem', lineHeight: 1.4 }}>
                {activeTab === 'my_shares'
                  ? !currentUserName?.trim()
                    ? 'Sign in with your username at the top right to access your personal gallery of shared clips.'
                    : 'Capture a scene in SoulCapture or open your SoulVault drawer in Studio and click Share to collaborate with friends.'
                  : activeTab === 'direct'
                  ? !currentUserName?.trim()
                    ? 'Sign in with your username at the top right to view scenes your crew shared directly with you.'
                    : 'Add friends to your crew above and share scenes directly with them to collaborate.'
                  : activeTab === 'following'
                  ? !currentUserName?.trim()
                    ? 'Sign in with your username at the top right to view clips from performers you follow.'
                    : 'Explore the Community Showcase and click "+ Follow" on your favorite performers to curate your custom feed!'
                  : 'Be the first to share a scene with the SoulSync community, or capture a new clip in SoulCapture!'}
              </p>
              {activeTab === 'my_shares' && (
                <button
                  type="button"
                  onClick={onNewClip}
                  className="btn-primary"
                  style={{ fontSize: '0.8rem', padding: '6px 14px' }}
                >
                  Capture a Scene to Share
                </button>
              )}
              {activeTab === 'following' && (
                <button
                  type="button"
                  onClick={() => setActiveTab('community')}
                  className="btn-primary"
                  style={{ fontSize: '0.8rem', padding: '6px 14px', background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)' }}
                >
                  Explore Community Showcase
                </button>
              )}
            </div>
          ) : (
            <div className="gallery-grid">
              {filteredClips.map((clip) => {
                const dubbed = clip.dubbedLinesCount || 0;
                const total = Math.max(1, clip.totalLinesCount || 4);
                const completionPct = Math.round((dubbed / total) * 100);

                return (
                  <div
                    key={clip.id}
                    className="entry-card"
                    onClick={() => handleCardClick(clip)}
                    title={`Click to open "${clip.title}" in Studio`}
                  >
                    {/* Poster */}
                    <div className="entry-poster">
                      <img
                        src={clip.posterUrl}
                        alt={clip.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=480&q=80';
                        }}
                      />

                      {/* Duration Badge */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '6px',
                          right: '6px',
                          background: 'rgba(0, 0, 0, 0.82)',
                          color: '#ffffff',
                          fontSize: '0.62rem',
                          padding: '1px 5px',
                          borderRadius: '3px',
                          fontWeight: 700
                        }}
                      >
                        {clip.duration}s
                      </div>

                      {/* Visibility Tag */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '6px',
                          left: '6px',
                          background:
                            clip.shareVisibility === 'public'
                              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.9) 100%)'
                              : 'linear-gradient(135deg, rgba(168, 85, 247, 0.9) 0%, rgba(147, 51, 234, 0.9) 100%)',
                          color: '#ffffff',
                          fontSize: '0.58rem',
                          padding: '1px 5px',
                          borderRadius: '3px',
                          fontWeight: 800,
                          letterSpacing: '0.03em',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.4)'
                        }}
                      >
                        {clip.shareVisibility === 'public' ? '🌐 PUBLIC' : '👥 DIRECT'}
                      </div>

                      {/* Collab Status Badge (Top-Right) */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          background:
                            clip.isEditable !== false
                              ? 'rgba(16, 185, 129, 0.9)'
                              : 'rgba(100, 116, 139, 0.85)',
                          color: '#ffffff',
                          fontSize: '0.58rem',
                          padding: '1px 5px',
                          borderRadius: '3px',
                          fontWeight: 800,
                          letterSpacing: '0.03em',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.4)'
                        }}
                      >
                        {clip.isEditable !== false ? '🤝 COLLAB' : '🎬 CLEAN'}
                      </div>

                      {/* Hover Overlay Play Icon */}
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(0, 0, 0, 0.35)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s ease'
                        }}
                        className="poster-play-overlay"
                      >
                        <span
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: 'rgba(168, 85, 247, 0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 0 16px rgba(168, 85, 247, 0.6)'
                          }}
                        >
                          <Play size={16} color="#ffffff" style={{ marginLeft: '2px' }} />
                        </span>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="entry-body">
                      <div>
                        {/* Title & Trim Info */}
                        <h3
                          style={{
                            fontSize: '0.86rem',
                            fontWeight: 700,
                            margin: '0 0 4px 0',
                            color: '#f8fafc',
                            lineHeight: 1.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {clip.title}
                        </h3>

                        {/* Trim & Stems info */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.66rem', color: '#94a3b8', marginBottom: '5px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Scissors size={10} color="#a78bfa" />
                            {clip.trimStart.toFixed(1)}s - {clip.trimEnd.toFixed(1)}s
                          </span>
                          <span style={{ color: clip.hasStems ? '#34d399' : '#fcd34d', display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Music size={9} />
                            {clip.hasStems ? 'Stems' : 'Pipeline'}
                          </span>
                        </div>

                        {/* Creator Attribution Chip & Follow Button */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '5px', marginBottom: '5px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                            <CharacterAvatar avatarId={clip.creatorAvatar || 'char-maverick'} size="xs" />
                            <span style={{ fontSize: '0.7rem', color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              by <strong style={{ color: '#f3e8ff' }}>@{clip.creatorName || clip.userName || 'Artist'}</strong>
                            </span>
                          </div>
                          <FollowButton
                            currentUserName={currentUserName}
                            targetUserName={clip.creatorName || clip.userName || ''}
                            size="sm"
                          />
                        </div>

                        {/* Share Note / Challenge Quote (if present) */}
                        {clip.shareMessage && (
                          <div
                            style={{
                              fontSize: '0.68rem',
                              color: '#cbd5e1',
                              background: 'rgba(255, 255, 255, 0.04)',
                              borderLeft: '2px solid #a855f7',
                              padding: '3px 6px',
                              borderRadius: '0 5px 5px 0',
                              marginBottom: '5px',
                              fontStyle: 'italic',
                              lineHeight: 1.25,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            "{clip.shareMessage}"
                          </div>
                        )}

                        {/* Completion Amount Progress Bar (x/y lines dubbed) */}
                        <div style={{ background: 'rgba(255, 255, 255, 0.04)', padding: '4px 6px', borderRadius: '5px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.64rem', marginBottom: '3px' }}>
                            <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Target size={10} color="#34d399" />
                              <span>{dubbed}/{total} Lines</span>
                            </span>
                            <span style={{ color: completionPct === 100 ? '#34d399' : '#a78bfa', fontWeight: 700 }}>
                              {completionPct}%
                            </span>
                          </div>
                          <div style={{ width: '100%', height: '3px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '9999px', overflow: 'hidden' }}>
                            <div
                              style={{
                                width: `${completionPct}%`,
                                height: '100%',
                                background: completionPct === 100
                                  ? 'linear-gradient(90deg, #10b981, #34d399)'
                                  : 'linear-gradient(90deg, #a855f7, #6366f1)',
                                borderRadius: '9999px'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Card Footer Actions */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingTop: '6px',
                          borderTop: '1px solid rgba(255, 255, 255, 0.06)'
                        }}
                      >
                        {/* Left: Like & Views Engagement */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={(e) => handleLikeClick(e, clip)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px',
                              background: 'transparent',
                              border: 'none',
                              color: (clip.likesCount || 0) > 0 ? '#f472b6' : '#94a3b8',
                              fontSize: '0.68rem',
                              cursor: 'pointer',
                              padding: 0
                            }}
                            title="Like this scene"
                          >
                            <Heart size={11} fill={(clip.likesCount || 0) > 0 ? '#f472b6' : 'none'} />
                            <span>{clip.likesCount || 0}</span>
                          </button>

                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.68rem', color: '#94a3b8' }} title="Total views">
                            <Eye size={11} />
                            <span>{clip.viewsCount || 0}</span>
                          </span>
                        </div>

                        {/* Right: Actions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {/* Copy Link Button — Friends/Direct shares tab only */}
                          {activeTab === 'direct' && (
                            <button
                              type="button"
                              onClick={(e) => handleCopyLink(e, clip)}
                              style={{
                                background: 'rgba(255, 255, 255, 0.06)',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                borderRadius: '4px',
                                color: '#cbd5e1',
                                cursor: 'pointer',
                                padding: '2px 5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                fontSize: '0.65rem'
                              }}
                              title="Copy link to this clip"
                            >
                              {copiedId === clip.id ? <Check size={10} color="#34d399" /> : <Copy size={10} />}
                              <span>{copiedId === clip.id ? 'Copied' : 'Link'}</span>
                            </button>
                          )}

                          {/* Delete button — My Shares tab only, subtle/understated until clicked */}
                          {activeTab === 'my_shares' && (
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (deletingId !== clip.id) {
                                  // First click: enter confirm state, auto-cancel after 3.5s
                                  setDeletingId(clip.id);
                                  setTimeout(() => setDeletingId((prev) => prev === clip.id ? null : prev), 3500);
                                } else {
                                  // Second click: execute delete
                                  if (isDeletingInFlight) return;
                                  setIsDeletingInFlight(true);
                                  const result = await deleteSharedClip(clip.id, currentUserName || clip.creatorName || 'Dan');
                                  setIsDeletingInFlight(false);
                                  setDeletingId(null);
                                  if (result.success) {
                                    setClips((prev) => prev.filter((c) => c.id !== clip.id));
                                  }
                                }
                              }}
                              style={{
                                background: deletingId === clip.id
                                  ? 'rgba(239, 68, 68, 0.25)'
                                  : 'transparent',
                                border: deletingId === clip.id
                                  ? '1px solid rgba(239, 68, 68, 0.6)'
                                  : '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '4px',
                                color: deletingId === clip.id ? '#fca5a5' : '#64748b',
                                cursor: 'pointer',
                                padding: deletingId === clip.id ? '3px 7px' : '3px 5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                fontSize: '0.65rem',
                                fontWeight: deletingId === clip.id ? 700 : 400,
                                opacity: deletingId === clip.id ? 1 : 0.65,
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (deletingId !== clip.id) {
                                  (e.currentTarget as HTMLElement).style.opacity = '1';
                                  (e.currentTarget as HTMLElement).style.color = '#94a3b8';
                                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (deletingId !== clip.id) {
                                  (e.currentTarget as HTMLElement).style.opacity = '0.65';
                                  (e.currentTarget as HTMLElement).style.color = '#64748b';
                                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.08)';
                                }
                              }}
                              title={deletingId === clip.id ? 'Click again to confirm deletion from SoulShare' : 'Delete entry'}
                            >
                              <Trash2 size={11} color={deletingId === clip.id ? '#fca5a5' : 'currentColor'} />
                              {deletingId === clip.id && <span>Confirm?</span>}
                            </button>
                          )}

                          {/* Open in Studio Dubbing Action Button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCardClick(clip);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px',
                              padding: '3px 8px',
                              borderRadius: '5px',
                              background:
                                clip.isEditable !== false
                                  ? 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)'
                                  : 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                              border: 'none',
                              color: '#ffffff',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              cursor: 'pointer',
                              boxShadow: clip.isEditable !== false
                                ? '0 0 8px rgba(168, 85, 247, 0.3)'
                                : '0 0 8px rgba(14, 165, 233, 0.3)'
                            }}
                          >
                            <Play size={9} fill="#fff" />
                            <span>{clip.isEditable !== false ? 'Join Collab' : 'Make Your Own'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Share Modal Fallback */}
      <ShareSoulClipModal
        isOpen={Boolean(sharingClip)}
        onClose={() => setSharingClip(null)}
        clip={sharingClip}
        currentUserName={currentUserName}
        onShareUpdated={(updated) => {
          setClips((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
          setSharingClip(null);
        }}
      />

      {/* ── Interactive Player Lightbox with Context-Aware Controls ── */}
      <ReviewShareLightbox
        isOpen={isLightboxOpen}
        onClose={() => {
          setIsLightboxOpen(false);
          setLightboxClip(null);
        }}
        clip={lightboxClip}
        stagedEntry={stagedClip && lightboxClip?.id === stagedClip.id ? stagedEntry : null}
        currentUserName={currentUserName}
        mode={lightboxMode}
        onPostSuccess={(newSharedClip) => {
          setClips((prev) => [newSharedClip, ...prev]);
          setLightboxClip(newSharedClip);
        }}
        onUpdateSuccess={(updatedClip) => {
          setClips((prev) => prev.map((c) => (c.id === updatedClip.id ? updatedClip : c)));
          setLightboxClip(updatedClip);
        }}
        onOpenInStudio={(clipToOpen, mode) => {
          setIsLightboxOpen(false);
          onOpenClipInStudio?.(clipToOpen as SoulClipItem, mode);
        }}
      />
    </div>
  );
};
